// Pestaña "Registro" de la pantalla del asistente: todo lo que hizo (o simuló) el asistente,
// con filtros por modo, herramienta, resultado y empresa. Paginado de a 50.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Filter, RefreshCw } from 'lucide-react';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { DataTable } from '../../components/shared/DataTable';
import { Pagination } from '../../components/shared/Pagination';
import { usePagination } from '../../hooks/usePagination';
import { EmailDetailDrawer } from './EmailDetailDrawer';
import { ActionResult, ActionDetail } from './ActionsMini';
import {
  loadMeta,
  asPage,
  optionsOf,
  actionModeLabel,
  toolLabel,
  fmtDateTime,
  ACTION_MODE_LABEL,
  ACTION_LABEL,
  type AssistantMeta,
  type ActionRow,
} from './shared';

type Row = ActionRow & Record<string, unknown>;

const MAX_PER_PAGE = 200;

const inputCls =
  'px-2 py-1 text-xs bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded text-[var(--color-text)]';
const linkCls = 'text-[var(--color-primary)] hover:underline';

/** Opciones del filtro "Qué hizo": etiqueta en español, se manda el nombre interno. Sin etiquetas repetidas. */
const TOOL_OPTIONS: Array<{ key: string; label: string }> = (() => {
  const seen = new Set<string>();
  const out: Array<{ key: string; label: string }> = [];
  for (const [key, label] of Object.entries(ACTION_LABEL)) {
    if (seen.has(label)) continue;
    seen.add(label);
    out.push({ key, label });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label, 'es'));
})();

export default function ActionsTab({
  onOpenCase,
  onChanged,
}: {
  onOpenCase?: (caseId: number) => void;
  /** Se avisa cuando algo cambió (p. ej. un reintento): refresca el contador de casos. */
  onChanged?: () => void;
}) {
  const sellers = useUIStore((s) => s.sellers);
  const pag = usePagination('assistant-actions');

  const [meta, setMeta] = useState<AssistantMeta | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [mode, setMode] = useState('');
  const [tool, setTool] = useState('');
  const [ok, setOk] = useState('');
  const [seller, setSeller] = useState('');
  const [openEmail, setOpenEmail] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reqSeq = useRef(0);

  useEffect(() => {
    let alive = true;
    void loadMeta().then((m) => {
      if (alive && m) setMeta(m);
    });
    return () => {
      alive = false;
    };
  }, []);

  const sellerName = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of sellers) map.set(s.id, s.nickname);
    return (id: number) => map.get(id) ?? String(id);
  }, [sellers]);

  useEffect(() => {
    if (pag.perPage > MAX_PER_PAGE) pag.setPerPage(MAX_PER_PAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pag.perPage]);

  const loadList = useCallback(async () => {
    if (pag.perPage > MAX_PER_PAGE) return;
    const seq = ++reqSeq.current;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: String(pag.perPage),
        offset: String((pag.page - 1) * pag.perPage),
      });
      if (mode) params.set('mode', mode);
      if (tool) params.set('tool', tool);
      if (ok) params.set('ok', ok);
      if (seller) params.set('seller_id', seller);
      const res = await api.get<unknown>(`/assistant/actions?${params}`);
      if (seq !== reqSeq.current) return;
      if (res.success) {
        const page = asPage<Row>(res.data);
        setRows(page.rows);
        pag.setTotal(page.total);
      } else {
        setRows([]);
        pag.setTotal(0);
        setError(res.error || 'No se pudo cargar el registro de acciones.');
      }
    } catch {
      if (seq === reqSeq.current) setError('No se pudo cargar el registro de acciones.');
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pag.page, pag.perPage, mode, tool, ok, seller]);

  useEffect(() => {
    void loadList();
  }, [loadList, reloadKey]);

  useEffect(() => {
    pag.resetPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, tool, ok, seller]);

  const clearFilters = () => {
    setMode('');
    setTool('');
    setOk('');
    setSeller('');
  };

  const modes = optionsOf(meta?.action_modes, ACTION_MODE_LABEL);

  const columns = [
    {
      key: 'created_at',
      label: 'Fecha',
      render: (r: Row) => (
        <span className="text-xs text-[var(--color-text-secondary)] whitespace-nowrap">{fmtDateTime(r.created_at)}</span>
      ),
    },
    {
      key: 'mode',
      label: 'Modo',
      render: (r: Row) => <span className="text-xs whitespace-nowrap">{actionModeLabel(r.mode)}</span>,
    },
    {
      key: 'seller_id',
      label: 'Empresa',
      render: (r: Row) => (
        <span className="text-xs whitespace-nowrap">{r.seller_id !== null ? sellerName(r.seller_id) : '—'}</span>
      ),
    },
    {
      key: 'tool',
      label: 'Qué hizo',
      render: (r: Row) => (
        <span className="text-xs" title={r.tool}>
          {toolLabel(r.tool)}
        </span>
      ),
    },
    {
      key: 'target',
      label: 'Sobre qué',
      render: (r: Row) => (
        <span className="text-xs block max-w-[180px] truncate" title={r.target ?? ''}>
          {r.target || '—'}
        </span>
      ),
    },
    {
      key: 'ok',
      label: 'Resultado',
      render: (r: Row) => <ActionResult a={r} />,
    },
    {
      key: 'detail',
      label: 'Detalle',
      render: (r: Row) => (
        <div className="text-xs max-w-[360px]">
          <ActionDetail a={r} />
        </div>
      ),
    },
    {
      key: 'email_id',
      label: 'Correo',
      render: (r: Row) =>
        r.email_id !== null ? (
          <button type="button" className={`text-xs ${linkCls}`} onClick={() => setOpenEmail(r.email_id)}>
            #{r.email_id}
          </button>
        ) : (
          <span className="text-xs text-[var(--color-text-muted)]">—</span>
        ),
    },
    {
      key: 'case_id',
      label: 'Caso',
      render: (r: Row) =>
        r.case_id !== null ? (
          onOpenCase ? (
            <button type="button" className={`text-xs ${linkCls}`} onClick={() => onOpenCase(r.case_id as number)}>
              #{r.case_id}
            </button>
          ) : (
            <span className="text-xs">#{r.case_id}</span>
          )
        ) : (
          <span className="text-xs text-[var(--color-text-muted)]">—</span>
        ),
    },
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm text-[var(--color-text-secondary)]">
        Todo lo que hizo el asistente. Las filas marcadas “simulado” son acciones que habría hecho: en modo
        aprendizaje se anotan pero no se ejecutan.
      </p>

      <div
        className="flex flex-wrap items-center gap-2 bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-3"
      >
        <Filter className="w-4 h-4 text-[var(--color-text-secondary)]" />
        <select value={mode} onChange={(e) => setMode(e.target.value)} className={inputCls} aria-label="Modo">
          <option value="">Todos los modos</option>
          {modes.map((m) => (
            <option key={m.key} value={m.key}>
              {m.label}
            </option>
          ))}
        </select>
        <select value={ok} onChange={(e) => setOk(e.target.value)} className={inputCls} aria-label="Resultado">
          <option value="">Todos los resultados</option>
          <option value="true">Salieron bien</option>
          <option value="false">Fallaron</option>
        </select>
        {sellers.length > 1 && (
          <select value={seller} onChange={(e) => setSeller(e.target.value)} className={inputCls} aria-label="Empresa">
            <option value="">Todas las empresas</option>
            {sellers.map((s) => (
              <option key={s.id} value={String(s.id)}>
                {s.nickname}
              </option>
            ))}
          </select>
        )}
        <select value={tool} onChange={(e) => setTool(e.target.value)} className={inputCls} aria-label="Qué hizo">
          <option value="">Todas las acciones</option>
          {TOOL_OPTIONS.map((o) => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={clearFilters}
          className="px-3 py-1 text-xs rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
        >
          Limpiar
        </button>
        <button
          type="button"
          onClick={() => setReloadKey((k) => k + 1)}
          title="Actualizar"
          aria-label="Actualizar"
          className="p-1 rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {error && (
        <div className="rounded-lg p-3 text-sm bg-[var(--color-error-bg,rgba(239,68,68,0.1))] text-[var(--color-error)]">
          {error}
        </div>
      )}

      <DataTable<Row>
        columns={columns}
        data={rows}
        loading={loading}
        emptyMessage="No hay acciones registradas con estos filtros."
      />

      <Pagination
        page={pag.page}
        perPage={pag.perPage}
        total={pag.total}
        totalPages={pag.totalPages}
        onPageChange={pag.setPage}
        onPerPageChange={pag.setPerPage}
      />

      {openEmail !== null && (
        <EmailDetailDrawer
          emailId={openEmail}
          sellerName={sellerName}
          onClose={() => setOpenEmail(null)}
          onChanged={() => {
            setReloadKey((k) => k + 1);
            onChanged?.();
          }}
          onOpenCase={
            onOpenCase
              ? (id) => {
                  setOpenEmail(null);
                  onOpenCase(id);
                }
              : undefined
          }
        />
      )}
    </div>
  );
}
